/**
 * The organization's brand, resolved ONCE per document load (ORDR-139).
 *
 * `Root` mounts this above every screen, so the brand read happens once at
 * startup, by host or by the `?brand=` pilot links carry, never by both. What
 * it resolves is only HELD here: whether a surface is drawn in it is
 * `BrandedSurface`'s decision, and the Order Manager (kitchen and staff) never
 * asks; it reads the tier with `useOrganizationBrand()`.
 *
 * Until the read answers, and for good when it fails, the tier is `null`, which
 * renders neutral. There is no third state: nothing here ever stands in a
 * platform brand for a missing one.
 */

import { useEffect, useState, type ReactElement, type ReactNode } from "react";
import type { BrandTier } from "@pirouz-ca/ui-tokens";
import { createApiClient, type ApiClient } from "../api/client.js";
import { brandLookupFor, fetchBrandTier, type BrandLookup } from "./brandApi.js";
import { OrganizationBrandContext } from "./organizationBrand.js";

export interface RuntimeBrandProviderProps {
  /** Injected for tests. Defaults to a client on `VITE_API_BASE_URL`. */
  client?: ApiClient;
  /** Injected for tests. Defaults to this document's host and `?brand=`. */
  lookup?: BrandLookup;
  children: ReactNode;
}

export function RuntimeBrandProvider({ client, lookup, children }: RuntimeBrandProviderProps): ReactElement {
  const [tier, setTier] = useState<BrandTier | null>(null);
  // Captured at mount: the address does not change under a mounted app, and a
  // second read would be a second chance to paint a second brand.
  const [startup] = useState(() => ({
    client: client ?? createApiClient(),
    lookup: lookup ?? brandLookupFor(window.location.host, window.location.search),
  }));

  useEffect(() => {
    let current = true;
    void fetchBrandTier(startup.client, startup.lookup).then((resolved) => {
      if (current) {
        setTier(resolved);
      }
    });
    return () => {
      current = false;
    };
  }, [startup]);

  return <OrganizationBrandContext.Provider value={tier}>{children}</OrganizationBrandContext.Provider>;
}
